// tslint:disable: jsx-no-lambda
import {
  Button,
  Checkbox,
  Divider,
  AccordionActions,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import {
  ColumnId,
  COLUMNS_ORDER,
  CreateExportUsersRequest,
  CreateExportOrganizationUsersRequest,
  CreateExportGuestUsersRequest,
  OrgExportColumnId,
  ORG_COLUMNS_ORDER
} from '../../../Apollo';
import ButtonLoading from 'Theme/components/ButtonLoading';
import TestIds from 'Tests/TestIds';
import { useParams } from 'react-router-dom';
import useNotification from 'Hooks/useNotification';
import { UsersScope } from '../UsersScope';

interface Column {
  label: string;
  ids: (ColumnId | OrgExportColumnId)[];
}

const ORG_USER_COLUMNS: Column[] = [
  {
    label: 'page.users.name',
    ids: [OrgExportColumnId.LastName]
  },
  {
    label: 'page.users.firstname',
    ids: [OrgExportColumnId.FirstName]
  },
  {
    label: 'page.users.export.email',
    ids: [OrgExportColumnId.Email]
  },
  {
    label: 'page.users.export.creationDate',
    ids: [OrgExportColumnId.CreationDate]
  },
  {
    label: 'page.users.export.status',
    ids: [OrgExportColumnId.Status]
  },
  {
    label: 'page.users.export.domain',
    ids: [OrgExportColumnId.Domain]
  }
];

const ORG_LISTS_COLUMNS: Column[] = [
  {
    label: 'page.users.export.accountCode',
    ids: [OrgExportColumnId.AccountCode]
  },
  {
    label: 'page.users.export.accountLabel',
    ids: [OrgExportColumnId.AccountLabel]
  },
  {
    label: 'page.users.export.accountAdministratorsCodes',
    ids: [OrgExportColumnId.AccountAdministratorsCode]
  },
  {
    label: 'page.users.export.accountAdministratorsLabels',
    ids: [OrgExportColumnId.AccountAdministratorsLabel]
  },
  {
    label: 'page.users.export.directoryLabels',
    ids: [OrgExportColumnId.DirectoryLabel]
  }
];

const GUEST_USER_COLUMNS: Column[] = [
  {
    label: 'page.users.name',
    ids: [ColumnId.LastName]
  },
  {
    label: 'page.users.firstname',
    ids: [ColumnId.FirstName]
  },
  {
    label: 'page.users.export.email',
    ids: [ColumnId.Email]
  },
  {
    label: 'page.users.export.status',
    ids: [ColumnId.Status]
  }
];

const GUEST_COLUMNS_ORDER = [
  ColumnId.LastName,
  ColumnId.FirstName,
  ColumnId.Email,
  ColumnId.Status
];

const USER_COLUMNS: Column[] = [
  {
    label: 'page.users.name',
    ids: [ColumnId.LastName]
  },
  {
    label: 'page.users.firstname',
    ids: [ColumnId.FirstName]
  },
  {
    label: 'page.users.export.email',
    ids: [ColumnId.Email]
  },
  {
    label: 'page.users.export.creationDate',
    ids: [ColumnId.CreationDate]
  },
  {
    label: 'page.users.export.status',
    ids: [ColumnId.Status]
  },
  {
    label: 'page.users.export.accountCode',
    ids: [ColumnId.AccountCode]
  },
  {
    label: 'page.users.export.accountLabel',
    ids: [ColumnId.AccountLabel]
  }
];

const LISTS_COLUMNS: Column[] = [
  {
    label: 'page.users.export.services',
    ids: [ColumnId.ServiceLabel, ColumnId.ServiceAccount]
  }
];

export const useStyles = makeStyles(theme => ({
  expansionPanelDetails: {
    flexDirection: 'column',
    padding: `${theme.spacing(2)} 0`
  },
  content: {
    padding: `0 ${theme.spacing(3)}`
  },
  heading: {
    marginBottom: theme.spacing(3)
  },
  table: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3)
  }
}));

interface Props {
  selectedUserIds: string[];
  selectAllUserEnabled: boolean;
  onCollapse: () => void;
  searchTerm: string;
  scope: UsersScope;
}

const ExportUsers = (props: Props) => {
  const {
    searchTerm,
    onCollapse,
    selectedUserIds,
    selectAllUserEnabled,
    scope
  } = props;
  const classes = useStyles();
  const { onError } = useNotification();
  const { accountCode } = useParams<IUriParams>();
  const [selectedColumns, setSelectedColumns] = React.useState<(ColumnId | OrgExportColumnId)[]>([]);

  const pickByContext = <T,>(guest: T, org: T, def: T): T => {
    switch (scope.kind) {
      case 'organizationGuests':
        return guest;
      case 'organization':
        return org;
      default:
        return def;
    }
  };

  const activeUserColumns = pickByContext(GUEST_USER_COLUMNS, ORG_USER_COLUMNS, USER_COLUMNS);
  const activeListsColumns = pickByContext<Column[]>([], ORG_LISTS_COLUMNS, LISTS_COLUMNS);
  const activeColumnsOrder = pickByContext<(ColumnId | OrgExportColumnId)[]>(
    GUEST_COLUMNS_ORDER,
    ORG_COLUMNS_ORDER,
    COLUMNS_ORDER
  );

  const onCompleted = (
    data:
      | CreateExportUsersRequestData
      | CreateExportOrganizationUsersRequestData
      | CreateExportGuestUsersRequestData
  ) => {
    let url: string;
    if ('createExportOrganizationUsersRequest' in data) {
      url = data.createExportOrganizationUsersRequest;
    } else if ('createExportGuestUsersRequest' in data) {
      url = data.createExportGuestUsersRequest;
    } else {
      url = data.createExportUsersRequest;
    }
    const iframe = globalThis.document.createElement('iframe');

    iframe.onload = () =>
      setTimeout(() => iframe.remove());
    iframe.style.display = 'none';
    iframe.src = url;

    globalThis.document.body.appendChild(iframe);

    setTimeout(() => {
      onCollapse();
    }, 1000);
  };

  const isAllUserFieldsChecked = activeUserColumns.every(column =>
    column.ids.every(id => selectedColumns.includes(id))
  );

  const isIndeterminate =
    !isAllUserFieldsChecked &&
    activeUserColumns.some(column =>
      column.ids.some(id => selectedColumns.includes(id))
    );

  const isAllListFieldsChecked = activeListsColumns.every(column =>
    column.ids.every(id => selectedColumns.includes(id))
  );

  const isListIndeterminate =
    !isAllListFieldsChecked &&
    activeListsColumns.some(column =>
      column.ids.some(id => selectedColumns.includes(id))
    );

  const [createExportUser, { loading }] = useMutation<
    CreateExportUsersRequestData,
    CreateExportUsersRequestVariables
  >(CreateExportUsersRequest, {
    onCompleted,
    onError
  });

  const [createExportOrganizationUser, { loading: orgLoading }] = useMutation<
    CreateExportOrganizationUsersRequestData,
    CreateExportOrganizationUsersRequestVariables
  >(CreateExportOrganizationUsersRequest, {
    onCompleted,
    onError
  });

  const [createExportGuestUser, { loading: guestLoading }] = useMutation<
    CreateExportGuestUsersRequestData,
    CreateExportGuestUsersRequestVariables
  >(CreateExportGuestUsersRequest, {
    onCompleted,
    onError
  });

  const isLoading = pickByContext(guestLoading, orgLoading, loading);

  const onToggleColumnsId = (ids: (ColumnId | OrgExportColumnId)[]) => {
    setSelectedColumns(
      ids.every(id => selectedColumns.includes(id))
        ? selectedColumns.filter(id => !ids.includes(id))
        : selectedColumns.concat(ids)
    );
  };

  const onToggleSelectAllUserColumns = () => {
    const obj = activeUserColumns.every(column =>
      column.ids.every(id => selectedColumns.includes(id))
    )
      ? selectedColumns.filter(
          id => !activeUserColumns.some(col => col.ids.includes(id))
        )
      : activeUserColumns.reduce(
          (selectedColumns: (ColumnId | OrgExportColumnId)[], col) =>
            selectedColumns.concat(
              // Only add the columns that were not selected already
              col.ids.filter(id => !selectedColumns.includes(id))
            ),
          selectedColumns
        );

    setSelectedColumns(obj);
  };

  const onToggleSelectAllListColumns = () => {
    const obj = activeListsColumns.every(column =>
      column.ids.every(id => selectedColumns.includes(id))
    )
      ? selectedColumns.filter(
          id => !activeListsColumns.some(col => col.ids.includes(id))
        )
      : activeListsColumns.reduce(
          (selectedColumns: (ColumnId | OrgExportColumnId)[], col) =>
            selectedColumns.concat(
              col.ids.filter(id => !selectedColumns.includes(id))
            ),
          selectedColumns
        );

    setSelectedColumns(obj);
  };

  return (
    <form
      onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isLoading || selectedColumns.length < 1) {
          return;
        }

        const filteredColumns = activeColumnsOrder.filter(columnId =>
          selectedColumns.includes(columnId)
        );

        if (scope.kind === 'organizationGuests') {
          createExportGuestUser({
            variables: {
              organizationCode: scope.organizationCode,
              personIdentifiers: selectAllUserEnabled ? null : selectedUserIds,
              textSearch: searchTerm,
              columns: filteredColumns
            }
          });
        } else if (scope.kind === 'organization') {
          createExportOrganizationUser({
            variables: {
              organizationCode: scope.organizationCode,
              personIdentifiers: selectAllUserEnabled ? null : selectedUserIds,
              textSearch: searchTerm,
              columns: filteredColumns
            }
          });
        } else {
          createExportUser({
            variables: {
              accountCode: accountCode === 'all' ? null : accountCode,
              personIdentifiers: selectAllUserEnabled ? null : selectedUserIds,
              textSearch: searchTerm,
              columns: filteredColumns
            }
          });
        }
      }}
      onReset={() => onCollapse()}
    >
      <Divider />
      <AccordionDetails classes={{ root: classes.expansionPanelDetails }}>
        <div className={classes.content}>
          <Typography variant="h4" classes={{ root: classes.heading }}>
            <FormattedMessage id="page.users.export.itemToExport" />
          </Typography>

          <Typography variant="h6" color="inherit">
            <FormattedMessage id="page.users.export.columns" />
          </Typography>
          <Typography variant="caption">
            <FormattedMessage id="page.users.export.help" />
          </Typography>
        </div>
        <Table classes={{ root: classes.table }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={isAllUserFieldsChecked}
                  onChange={onToggleSelectAllUserColumns}
                  indeterminate={isIndeterminate}
                />
              </TableCell>
              <TableCell align="left">
                <Typography>
                  <FormattedMessage id="page.users.export.columns" />
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeUserColumns.map(column => {
              const isChecked = column.ids.every(id =>
                selectedColumns.includes(id)
              );
              return (
                <TableRow
                  key={column.ids.join('-')}
                  onClick={() => onToggleColumnsId(column.ids)}
                  role="checkbox"
                  aria-checked={isChecked}
                  tabIndex={-1}
                  selected={isChecked}
                  hover={true}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isChecked}
                      name={column.label}
                    />
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="body2">
                      <FormattedMessage id={column.label} />
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {activeListsColumns.length > 0 && (
          <>
            <div className={classes.content}>
              <Typography variant="h6" color="inherit">
                <FormattedMessage id="page.users.export.lists" />
              </Typography>
              <Typography variant="caption">
                <FormattedMessage id="page.users.export.needServiceList" />
              </Typography>
            </div>
            <Table classes={{ root: classes.table }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {activeListsColumns.length > 1 && (
                      <Checkbox
                        color="primary"
                        checked={isAllListFieldsChecked}
                        onChange={onToggleSelectAllListColumns}
                        indeterminate={isListIndeterminate}
                      />
                    )}
                  </TableCell>
                  <TableCell align="left">
                    <Typography>
                      <FormattedMessage id="page.users.export.lists" />
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeListsColumns.map(column => {
                  const isChecked = column.ids.every(id =>
                    selectedColumns.includes(id)
                  );
                  return (
                    <TableRow
                      key={column.ids.join('-')}
                      onClick={() => onToggleColumnsId(column.ids)}
                      role="checkbox"
                      aria-checked={isChecked}
                      tabIndex={-1}
                      selected={isChecked}
                      hover={true}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isChecked}
                          name={column.label}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="body2">
                          <FormattedMessage id={column.label} />
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </AccordionDetails>
      <Divider />
      <AccordionActions>
        <Button
          type="reset"
          color='inherit'
          data-testid={TestIds.common.snackbar.action.cancel}
        >
          <FormattedMessage id="common.action.cancel" />
        </Button>
        <ButtonLoading
          type="submit"
          color="primary"
          variant="contained"
          loading={isLoading}
        >
          <FormattedMessage id="common.action.export" />
        </ButtonLoading>
      </AccordionActions>
    </form>
  );
};

export default ExportUsers;
